import * as Log from "web-ui/log"

// A descriptor key is Stimulus's own action grammar for the event part
// (parseActionDescriptorString), so keydown.esc, ctrl+click, and @window mean
// what they already mean in data-action. This pattern matches the event part,
// which splitKey has already separated from the selector. Every character class
// excludes whitespace, so any whitespace the split left behind fails to parse
// rather than parsing as an event name containing a space.
// - Aaron, Sun Aug 23 2026
const descriptorPattern = /^(?:([^\s.+]+)\+)?([^\s.@]+)(?:\.([^\s@]+))?(?:@(window|document))?$/

const keyFilterEventNames = [
  "keydown",
  "keyup",
  "keypress"
]

const modifierKeyNames = [
  "meta",
  "ctrl",
  "alt",
  "shift"
]

const nativeOptionNames = [
  "capture",
  "once",
  "passive"
]

// These events do not bubble, so a listener on an ancestor never receives them
// in the bubble phase. Capture always traverses the ancestor chain down to the
// target, so a descriptor for one of them captures and an engineer writes
// "toggle" without knowing the difference. - Aaron, Sun Aug 23 2026
const nonBubblingEventNames = [
  "abort",
  "beforetoggle",
  "blur",
  "canplay",
  "canplaythrough",
  "cuechange",
  "durationchange",
  "emptied",
  "ended",
  "error",
  "focus",
  "invalid",
  "load",
  "loadeddata",
  "loadedmetadata",
  "loadstart",
  "mouseenter",
  "mouseleave",
  "pause",
  "play",
  "playing",
  "pointerenter",
  "pointerleave",
  "progress",
  "ratechange",
  "scroll",
  "scrollend",
  "seeked",
  "seeking",
  "stalled",
  "suspend",
  "timeupdate",
  "toggle",
  "volumechange",
  "waiting"
]

export function eventDescriptors(controller) {
  // A subclass that declares its own events table shadows its ancestors'
  // tables, which is plain static-field resolution. Stimulus unions its own
  // static declarations up the prototype chain
  // (readInheritableStaticObjectPairs), so an author of a controller hierarchy
  // could expect events to behave the same way. It does not, because a
  // hierarchy that declares events at two levels has not come up, and the merge
  // costs a spec and a fixture section it would have to earn.
  // - Aaron, Sun Aug 23 2026
  const events = controller.constructor.events || {}

  const descriptors = []

  for (const [key, value] of Object.entries(events)) {
    const descriptor = parseDescriptor(controller, key, value)

    if (!descriptor) continue

    descriptors.push(descriptor)
  }

  return descriptors
}

export function rootName(root) {
  if (root === window) return "window"
  if (root === document) return "document"

  return "element"
}

// The order reproduces Binding#handleEvent: the key filter and the matched
// element decide whether the descriptor applies at all, and only then do the
// option filters run, so stop and prevent take effect only for an event the
// handler would otherwise receive. - Aaron, Sun Aug 23 2026
export function handleEvent(controller, event, descriptor) {
  if (keyFilterRejects(controller, event, descriptor)) return

  const element = matchedElement(controller, event, descriptor)
  if (!element) return

  if (!optionFiltersPass(controller, event, element, descriptor)) return

  debug("Handling event", { identifier: controller.identifier, event: descriptor.key, handler: descriptor.value })

  invokeMethod(controller, event, element, descriptor)
}

export function matchedElement(controller, event, descriptor) {
  if (!descriptor.selector) return controller.element

  // The target is the document for a keydown that lands on nothing focusable
  // and the window for a resize, and neither of those has closest.
  // Binding#willBeInvokedByEvent guards with the same check.
  // - Aaron, Sun Aug 23 2026
  if (!(event.target instanceof Element)) return null

  const element = event.target.closest(descriptor.selector)
  if (!element) return null

  // A descriptor rooted at the window or the document reaches outside the
  // controller, so scope does not apply to it. - Aaron, Sun Aug 23 2026
  if (descriptor.root !== controller.element) return element

  // Scope#containsElement is the check every data-action in the application
  // already runs through, so a nested controller of the same identifier keeps
  // its own subtree here for the same reason it does there. It also subsumes
  // containment: closest walks up from the target and can match an ancestor of
  // the controller element, and no such ancestor can have the controller
  // element as its own nearest controller. - Aaron, Sun Aug 23 2026
  if (!controller.scope.containsElement(element)) return null

  return element
}

export function debug(message, data) {
  Log.debug("Controller - " + message, data)
}

function parseDescriptor(controller, key, value) {
  const { eventKey, selector } = splitKey(key)

  const matches = eventKey.match(descriptorPattern)

  if (!matches) {
    reportError(controller, key, value, "cannot be parsed")
    return
  }

  let event = matches[2]
  let dottedKeyFilter = matches[3]

  // A dotted suffix on anything but a keyboard event is part of the event name,
  // which is how Stimulus admits an event name containing a period
  // (parseActionDescriptorString). - Aaron, Sun Aug 23 2026
  if (dottedKeyFilter && !keyFilterEventNames.includes(event)) {
    event = `${event}.${dottedKeyFilter}`
    dottedKeyFilter = ""
  }

  const keyFilter = matches[1] || dottedKeyFilter || ""

  if (!keyFilterValid(controller, key, value, keyFilter)) return

  const tokens = value.split(":")
  const method = tokens.shift()
  const options = parseOptions(tokens)

  if (!optionsValid(controller, key, value, options)) return

  if (!("capture" in options) && nonBubblingEventNames.includes(event)) {
    options.capture = true
  }

  const root = descriptorRoot(controller, matches[4])

  if (typeof controller[method] !== "function") {
    reportError(controller, key, value, `names undefined method "${method}"`)
    return
  }

  return { key, value, event, keyFilter, root, options, selector, method }
}

// The first whitespace in the key separates the event part from the selector,
// so a selector containing a descendant combinator or a quoted space arrives
// whole. - Aaron, Sun Aug 23 2026
function splitKey(key) {
  const separatorIndex = key.search(/\s/)

  if (separatorIndex === -1) return { eventKey: key, selector: "" }

  return { eventKey: key.slice(0, separatorIndex), selector: key.slice(separatorIndex).trim() }
}

// Reproduces Stimulus's own option parsing (parseEventOptions), so a leading
// exclamation point means what it means in an action. - Aaron, Sun Aug 23 2026
function parseOptions(tokens) {
  return tokens.reduce((options, token) => {
    return Object.assign(options, { [token.replace(/^!/, "")]: !token.startsWith("!") })
  }, {})
}

// Stimulus skips an option name it does not recognize
// (Binding#applyEventModifiers). This facility reports it, because a misspelled
// option name is a handler that silently behaves differently than it reads,
// which is the failure mode the facility exists to remove.
// - Aaron, Sun Aug 23 2026
function optionsValid(controller, key, value, options) {
  const { actionDescriptorFilters } = controller.application

  for (const name of Object.keys(options)) {
    if (nativeOptionNames.includes(name)) continue
    if (name in actionDescriptorFilters) continue

    reportError(controller, key, value, `uses unknown option "${name}"`)
    return false
  }

  return true
}

function keyFilterValid(controller, key, value, keyFilter) {
  if (!keyFilter) return true

  const keyNames = keyFilter.split("+").filter((name) => !modifierKeyNames.includes(name))
  const keyName = keyNames[0]

  if (!keyName) return true

  if (!(keyName in keyMappings(controller))) {
    reportError(controller, key, value, `uses unknown key filter "${keyFilter}"`)
    return false
  }

  return true
}

function descriptorRoot(controller, name) {
  if (name === "window") return window
  if (name === "document") return document

  return controller.element
}

// Mirrors Binding#applyEventModifiers. Delegating to the application's filter
// table is what makes stop, prevent, and self behave as they do in a Stimulus
// action, and what makes an option registered through
// Application#registerActionOption work with no code here. A name with no
// filter is an option the browser interprets, so it is skipped.
// - Aaron, Sun Aug 23 2026
function optionFiltersPass(controller, event, element, descriptor) {
  const { actionDescriptorFilters } = controller.application

  let passes = true

  for (const [name, value] of Object.entries(descriptor.options)) {
    if (!(name in actionDescriptorFilters)) continue

    const filter = actionDescriptorFilters[name]

    passes = passes && filter({ name, value, event, element, controller })
  }

  return passes
}

function keyFilterRejects(controller, event, descriptor) {
  if (!descriptor.keyFilter) return false

  if (event instanceof KeyboardEvent) {
    return keyboardEventRejects(controller, event, descriptor)
  }

  if (event instanceof MouseEvent) {
    return modifierKeysDissatisfied(event, [descriptor.keyFilter])
  }

  return false
}

function keyboardEventRejects(controller, event, descriptor) {
  const filters = descriptor.keyFilter.split("+")

  if (modifierKeysDissatisfied(event, filters)) return true

  const keyNames = filters.filter((name) => !modifierKeyNames.includes(name))
  const keyName = keyNames[0]

  if (!keyName) return false

  return keyMappings(controller)[keyName].toLowerCase() !== event.key.toLowerCase()
}

function modifierKeysDissatisfied(event, filters) {
  const [meta, ctrl, alt, shift] = modifierKeyNames.map((name) => filters.includes(name))

  return event.metaKey !== meta || event.ctrlKey !== ctrl || event.altKey !== alt || event.shiftKey !== shift
}

function invokeMethod(controller, event, element, descriptor) {
  try {
    controller[descriptor.method](event, element)
  } catch (error) {
    const message = `invoking event descriptor "${descriptor.key}" -> "${descriptor.value}" for "${controller.identifier}"`

    controller.application.handleError(error, message, { identifier: controller.identifier, element, event })
  }
}

function keyMappings(controller) {
  return controller.application.schema.keyMappings
}

function reportError(controller, key, value, problem) {
  const message = `Event descriptor "${key}" -> "${value}" ${problem}`
  const error = new Error(message)

  controller.application.handleError(error, message, { identifier: controller.identifier, element: controller.element })
}
